import { Button, MenuItem, Paper, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
} from '@mui/x-data-grid';
import { Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import * as Yup from 'yup';
import {
  FormikSelect,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import { useMakePHPRequest } from '../../../common/useMakePHPRequest';
import { trackSaveAddNumber } from '../../../common/userTracking';
import { user } from '../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../../modules/Manage/pages/queues-table/DataTable';
import { countryGroups, countryList } from '../../subpages/countryList';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { BuyNumberConfirmationModal } from './BuyNumberConfirmationModal';
import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    flex: '1 1 0',
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  spaceHorizontally: {
    marginRight: theme.spacing(2),
    '&:last-child': {
      marginRight: 0,
    },
  },
  button: {
    // color: theme.palette.primary.contrastText,
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiButton-label': {
      padding: 2,
    },
  },
}));
export function AddNumberModal({
  show,
  handleClose,
  refetchBusinessNumbers,
  businessSMSRulesLoadable,
  setRefetchToggleForSMSRules,
  productData,
}) {
  // states for buy number confirmation modal dialog
  const [showBuy, setShowBuy] = useState(false);
  const handleCloseBuy = () => setShowBuy(false);
  const handleShowBuy = () => setShowBuy(true);
  const [numberToBuy, setNumberToBuy] = useState(0);
  const [rate, setRate] = useState(0);
  const [numType, setNumType] = useState('Local');
  const sortModel: GridSortModel = [{ field: 'number', sort: 'desc' }];
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const userAtom = useRecoilValueLoadable(user);
  const businessSmsEnabled = process.env.VITE_APP_BUSINESS_SMS === '1';

  const makeRequest = useMakePHPRequest({
    userAtom: userAtom.contents,
  });
  const data = {
    call: methodNameEnum.GET_BUSINESS_CONFERENCES,
  };
  const businessConferences = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CONFERENCES,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const conferences = useMemo(
    () =>
      businessConferences.state === 'hasValue'
        ? [
            ...(businessConferences.contents?.response?.data?.result ?? []),
          ]?.sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          )
        : [],
    [businessConferences]
  );
  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );
  const activeUsers = useMemo(
    () =>
      businessUsers.state === 'hasValue'
        ? [...(businessUsers.contents?.response?.data?.result ?? [])]
            ?.filter((user) => user.isactive === 1)
            ?.sort((a, b) =>
              a.fname.toLowerCase().localeCompare(b.fname.toLowerCase())
            )
        : [],
    [businessUsers]
  );
  const dataRingGroups = {
    call: methodNameEnum.GET_BUSINESS_GROUPS,
  };
  const ringGroups = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_GROUPS,
      payloadJsonString: JSON.stringify(dataRingGroups),
    })
  );
  const groups = useMemo(
    () =>
      ringGroups.state === 'hasValue'
        ? [
            ...(ringGroups.contents?.response?.data?.result ?? []),
          ]?.sort((a, b) =>
            a.groupname.toLowerCase().localeCompare(b.groupname.toLowerCase())
          )
        : [],
    [ringGroups]
  );
  const businessIvrs = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_IVRS })
  );

  const ivrs = useMemo(
    () =>
      businessIvrs.state === 'hasValue'
        ? [...(businessIvrs.contents?.data?.result ?? [])]?.sort((a, b) =>
            a.IVRname.toLowerCase().localeCompare(b.IVRname.toLowerCase())
          )
        : [],
    [businessIvrs]
  );
  const businessQueues = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );
  const queues = useMemo(
    () =>
      businessQueues.state === 'hasValue'
        ? [...(businessQueues.contents?.data?.result ?? [])]?.sort((a, b) =>
            a.queuename.toLowerCase().localeCompare(b.queuename.toLowerCase())
          )
        : [],
    [businessQueues]
  );

  const addNumberSchema = useMemo(
    () =>
      Yup.object({
        numberName: Yup.string().max(
          100,
          'Name must be a maximum of 100 characters'
        ),
      }),
    []
  );

  const numberForOptions = [
    'None',
    'Agent',
    'Boost Set',
    'Conference',
    'Group',
    'IVR',
    'Queue',
  ];
  const smsOptions = [
    {
      code: 'Y',
      name: 'Yes',
    },
    {
      code: 'E',
      name: 'Either',
    },
  ];
  const numTypes = ['Local', 'Tollfree', 'Mobile', 'Any'];
  function priceFormatter(params: GridRenderCellParams) {
    switch (numType) {
      case 'Local':
        return `$${productData?.localLine?.discountedPrice ?? 5}`;
      case 'Tollfree':
        return `$${productData?.tollFreeLine?.discountedPrice ?? 5}`;
      case 'Any':
        if (params.row.type === 'fixed') {
          return `$${productData?.localLine?.discountedPrice ?? 5}`;
        } else if (params.row.type === 'tollfree') {
          return `$${productData?.tollFreeLine?.discountedPrice ?? 5}`;
        } else {
          return `$${params.row.monthly_rental_rate ?? 5}`;
        }
    }
    return `$${params.row.monthly_rental_rate ?? 5}`;
  }
  function mmsFormatter(params: GridRenderCellParams) {
    if (params.row.mmsEnabled) {
      return <b>Yes</b>;
    } else {
      return <b>No</b>;
    }
  }
  function smsFormatter(params: GridRenderCellParams) {
    if (params.row.smsEnabled) {
      return <b>Yes</b>;
    } else {
      return <b>No</b>;
    }
  }

  function handleBuyNumber(row: GridRowData) {
    setNumberToBuy(row.number);
    setRate(row.monthly_rental_rate);
    handleShowBuy();
    // console.log(row, 'find num id ');
  }
  function buyNumberFormatter(params: GridRenderCellParams) {
    return (
      <Button
        className={classes.button}
        onClick={() => handleBuyNumber(params.row)}
        variant="contained"
        color="primary"
      >
        Buy
      </Button>
    );
  }
  const columns = [
    {
      field: 'number',
      headerName: 'Number',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'region',
      headerName: 'Region',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'monthly_rental_rate',
      headerName: 'Price',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: priceFormatter,
      width: 110,
    },
    {
      field: 'mmsEnabled',
      headerName: 'MMS',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: mmsFormatter,
      width: 110,
    },
    {
      field: 'smsEnabled',
      headerName: 'SMS',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: smsFormatter,
      width: 110,
    },
    {
      field: 'type',
      headerName: 'Buy',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: buyNumberFormatter,
    },
  ] /* satisfies */ as IColDef[];

  const businessSMSOptions = useMemo(
    () =>
      businessSMSRulesLoadable.state === 'hasValue' &&
      businessSMSRulesLoadable?.contents?.response?.data?.result?.length
        ? [
            { businessSMSId: '', name: 'None' },
            ...(businessSMSRulesLoadable?.contents?.response?.data?.result ??
              []),
          ]
        : [],
    [businessSMSRulesLoadable]
  );

  React.useEffect(() => {
    setRefetchToggleForSMSRules({});
  }, []);
  const customPaginationActions = (props) => {
    return null;
  };

  return (
    <Paper elevation={0} square={true}>
      <Formik
        onSubmit={async (values, actions) => {
          setNumType(values.numType);
          // console.log(values, 'valuees in submit');
          const { response } = await makeRequest({
            methodName: methodNameEnum.PHONE_NUMBER_SEARCH,
            payload: {
              cc: values.country,
              pattern: values.areaCode,
              numtype: values.numType.toLocaleLowerCase(),
              sms: values.smsAble,
            },
          });
          // console.log(response, 'response of phone number search');
          if (response?.data.success === 'false') {
            enqueueSnackbarWithStyles({
              message: response.data.message,
              alertProps: { severity: 'warning' },
            });
          }
          if (response?.data?.response?.objects?.length >= 1) {
            actions.setFieldValue(
              'availableNumbers',
              response?.data?.response?.objects.map((o) => ({
                ...o,
                id: o.number,
              }))
            );
          }
          const numbersTable = document.querySelector('#availableNumbers');
          if (numbersTable) {
            numbersTable.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }}
        validationSchema={addNumberSchema}
        enableReinitialize={true}
        initialValues={{
          numberFor: 'None',
          agent: '',
          conference: '',
          group: '',
          ivr: '',
          queue: '',
          country: 'US',
          areaCode: '',
          smsAble: 'Y',
          numType: 'Local',
          availableNumbers: [],
          numberName: '',
          businessSMSId: '',
        }}
      >
        {(formik) => (
          <ModalWithStyles
            className="addNumberModal"
            onSubmit={formik.handleSubmit}
            title="Add New Number"
            aria-labelledby="example-modal-sizes-title-lg"
            show={show}
            onHide={() => handleClose()}
            isSubmitting={formik.isSubmitting}
            isLoading={formik.isSubmitting}
            size="lg"
            footerComponent={
              <div className="col">
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <Button
                    className={classes.button}
                    disableElevation
                    variant="contained"
                    disabled={formik.isSubmitting}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            }
          >
            <div className="container mt-3 modalContent">
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Number For</h5>
                  <p className="fieldDescription">
                    Choose from the dropdown to designate this number for a
                    specific Kixie entity like Agent, Queue, or others. Calling
                    this number will route the call to the selected entity.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikSelect
                    id="standard-basic"
                    label="Number For"
                    name="numberFor"
                    formik={formik}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    fullWidth
                  >
                    {numberForOptions.map((option, index) => (
                      <MenuItem key={index} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </div>
              </div>
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Country</h5>
                  <p className="fieldDescription">
                    (Optional) Select the country from which you’d like to
                    purchase a number. This will determine the pool of numbers
                    available for your selection.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikSelect
                    name="country"
                    formik={formik}
                    fullWidth
                    label="Country"
                    margin="dense"
                    size="small"
                    variant="outlined"
                    native
                  >
                    {countryGroups.map((
                      group // print out optgroup(s)
                    ) => (
                      <optgroup key={group} label={group}>
                        {countryList
                          .filter((country) => country.groups?.includes(group))
                          .map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                    {countryList.map((
                      country // print out options
                    ) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </FormikSelect>
                </div>
              </div>
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">SMSable</h5>
                  <p className="fieldDescription">
                    Specify whether the new number should be able to send and
                    receive SMS messages.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikSelect
                    name="smsAble"
                    className={classes.field}
                    formik={formik}
                    fullWidth
                    label="SMSable"
                    margin="dense"
                    size="small"
                    variant="outlined"
                  >
                    {smsOptions.map((sms, index) => (
                      <MenuItem key={index} value={sms.code}>
                        {sms.name}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </div>
              </div>
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Number Name</h5>
                  <p className="fieldDescription">
                    Create a name for the number to easily identify it on the
                    Numbers page and when viewing call reports.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikTextField
                    id="standard-basic"
                    label="Name"
                    name="numberName"
                    formik={formik}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    fullWidth
                  />
                </div>
              </div>
              {formik.values.numberFor === 'Agent' && (
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Select Agent</h5>
                    <p className="fieldDescription">
                      Choose a specific agent from the dropdown to apply this
                      number to the selection.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Select Agent"
                      margin="dense"
                      size="small"
                      variant="outlined"
                      multiple={false}
                      options={activeUsers}
                      required
                      onChange={(event, newValue) =>
                        formik.setFieldValue(
                          'agent',
                          newValue ? newValue.userid : ''
                        )
                      }
                      getOptionLabel={(option) =>
                        option ? `${option?.fname} ${option?.lname}` : ''
                      } // collapse to empty string if no option found
                      isOptionEqualToValue={(option, value) =>
                        option?.userid === value?.userid
                      }
                      renderOption={(props, option) => {
                        const optionProps = { ...props, key: option.userid };
                        return (
                          <li {...optionProps}>
                            {`${option?.fname} ${option?.lname}`}
                          </li>
                        );
                      }}
                    />
                  </div>
                </div>
              )}
              {formik.values.numberFor === 'Conference' && (
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Select Conference</h5>
                    <p className="fieldDescription">
                      Choose a specific conference from the dropdown to apply
                      this number to the selection.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Select Conference"
                      margin="dense"
                      size="small"
                      variant="outlined"
                      multiple={false}
                      options={conferences}
                      onChange={(event, newValue) =>
                        formik.setFieldValue(
                          'conference',
                          newValue ? newValue.conferenceid : ''
                        )
                      }
                      getOptionLabel={(option) =>
                        option ? `${option.name}` : ''
                      } // collapse to empty string if no option found
                      isOptionEqualToValue={(option, value) =>
                        option?.conferenceid === value?.conferenceid
                      }
                    />
                  </div>
                </div>
              )}
              {formik.values.numberFor === 'Group' && (
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Select Group</h5>
                    <p className="fieldDescription">
                      Choose a specific group from the dropdown to apply this
                      number to the selection.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Select Group"
                      margin="dense"
                      variant="outlined"
                      multiple={false}
                      options={groups}
                      onChange={(event, newValue) =>
                        formik.setFieldValue(
                          'group',
                          newValue ? newValue.maingroupid : ''
                        )
                      }
                      getOptionLabel={(option) =>
                        option ? `${option.groupname}` : ''
                      } // collapse to empty string if no option found
                      isOptionEqualToValue={(option, value) =>
                        option?.maingroupid === value?.maingroupid
                      }
                    />
                  </div>
                </div>
              )}
              {formik.values.numberFor === 'IVR' && (
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Select IVR</h5>
                    <p className="fieldDescription">
                      Choose a specific IVR from the dropdown to apply this
                      number to the selection.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Select IVR"
                      margin="dense"
                      variant="outlined"
                      multiple={false}
                      options={ivrs}
                      onChange={(event, newValue) =>
                        formik.setFieldValue(
                          'ivr',
                          newValue ? newValue.mainIVRid : ''
                        )
                      }
                      getOptionLabel={(option) =>
                        option ? `${option.IVRname}` : ''
                      } // collapse to empty string if no option found
                      isOptionEqualToValue={(option, value) =>
                        option?.mainIVRid === value?.mainIVRid
                      }
                    />
                  </div>
                </div>
              )}
              {formik.values.numberFor === 'Queue' && (
                <div className="row borderBottom">
                  <div className="col-lg-8">
                    <h5 className="fieldTitle">Select Queue</h5>
                    <p className="fieldDescription">
                      Choose a specific queue from the dropdown to apply this
                      number to the selection.
                    </p>
                  </div>
                  <div className="col-lg-4">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Select Queue"
                      margin="dense"
                      variant="outlined"
                      multiple={false}
                      options={queues}
                      onChange={(event, newValue) =>
                        formik.setFieldValue(
                          'queue',
                          newValue ? newValue.queueid : ''
                        )
                      }
                      getOptionLabel={(option) =>
                        option ? `${option.queuename}` : ''
                      } // collapse to empty string if no option found
                      isOptionEqualToValue={(option, value) =>
                        option?.queueid === value?.queueid
                      }
                    />
                  </div>
                </div>
              )}
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Area Code</h5>
                  <p className="fieldDescription">
                    (Optional) Enter an area code to filter available numbers
                    based on a specific geographic region.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikTextField
                    id="standard-basic"
                    label="Area Code"
                    name="areaCode"
                    formik={formik}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                  />
                </div>
              </div>
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Local/Tollfree</h5>
                  <p className="fieldDescription">
                    Choose whether you need a Local or Tollfree number based on
                    your intended usage.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikSelect
                    style={{ marginTop: '3px' }}
                    id="standard-basic"
                    label="Local/Tollfree"
                    name="numType"
                    formik={formik}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                  >
                    {numTypes.map((num, index) => (
                      <MenuItem key={index} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </div>
              </div>
              {businessSmsEnabled &&
                formik.values.numberFor !== 'Agent' &&
                businessSMSRulesLoadable.state === 'hasValue' &&
                businessSMSOptions.length > 0 && (
                  <div className="row">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Select Team SMS Rule</h5>
                      <p className="fieldDescription">
                        (Optional) Choose a Team SMS Rule to determine how
                        inbound SMS messages are handled and which agents
                        receive them as part of the Team SMS.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikSelect
                        style={{ marginTop: '3px' }}
                        id="standard-basic"
                        label="Select Team SMS Rule"
                        formik={formik}
                        onChange={
                          ((event, newValue) =>
                            formik.setFieldValue(
                              'businessSMSId',
                              newValue ?? ''
                            )) as any
                        }
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        name="businessSMSId"
                      >
                        {businessSMSOptions.map((rule, index) => (
                          <MenuItem key={index} value={rule.businessSMSId}>
                            {rule.name}
                          </MenuItem>
                        ))}
                      </FormikSelect>
                    </div>
                  </div>
                )}
              <div
                className="row"
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  className={`searchBtn ${classes.button}`}
                  disableElevation
                  variant="contained"
                  color="primary"
                  // disabled={formik.isSubmitting || !formik.dirty}
                  onClick={() => {
                    trackSaveAddNumber();
                    formik.handleSubmit();
                  }}
                >
                  Search for available numbers
                </Button>
              </div>
              <div>
                {formik.values.availableNumbers.length > 1 && (
                  <div id="availableNumbers">
                    <DataTable
                      columns={columns}
                      rows={formik.values.availableNumbers}
                      sortModel={sortModel}
                      hideSortIcon={true}
                      customPaginationActions={customPaginationActions}
                      hideRowsPerPageOptions={true}
                    />
                  </div>
                )}
                <BuyNumberConfirmationModal
                  formik={formik}
                  refetchBusinessNumbers={refetchBusinessNumbers}
                  formikValues={formik.values}
                  rate={rate}
                  number={numberToBuy}
                  show={showBuy}
                  handleClose={handleCloseBuy}
                  methodNameEnum={methodNameEnum}
                  makeRequest={makeRequest}
                  enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                  handleCloseOuter={handleClose}
                />
              </div>
            </div>
          </ModalWithStyles>
        )}
      </Formik>
    </Paper>
  );
}
